import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  ExternalLink,
  ShieldAlert,
  Search,
  Check,
  X,
  Sparkles,
  Coins,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import {
  CustomCourseRequestsClient,
  type AdminCustomCourseRequest,
  type AdminCustomCourseDocument,
} from "@/components/admin/custom-courses/CustomCourseRequestsClient";

export type { AdminCustomCourseRequest, AdminCustomCourseDocument };

interface OrgRequest {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  status: "pending" | "approved" | "rejected";
  industry?: string | null;
  company_size?: string | null;
  allowed_domains: string[];
  expected_skills: string[];
  target_roles: string[];
  departments: string[];
  rejection_reason?: string | null;
  applicant_name?: string | null;
  applicant_email?: string | null;
  total_members: number;
  created_at: string;
}

export default function AdminOrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") === "course_requests" ? "course_requests" : "organizations";

  const [requests, setRequests] = useState<OrgRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  // Reject Modal State for Organizations
  const [rejectModalOrg, setRejectModalOrg] = useState<OrgRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Super Admin Custom Course Requests State
  const [customCourseRequests, setCustomCourseRequests] = useState<AdminCustomCourseRequest[]>([]);
  const [loadingCourseRequests, setLoadingCourseRequests] = useState(false);

  const handleTabChange = (tab: "organizations" | "course_requests") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "course_requests") {
        next.set("tab", "course_requests");
      } else {
        next.delete("tab");
      }
      return next;
    });
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await api.listOrganizationRequestsApiV1AdminOrganizationRequestsGet();
      if (!error && data) {
        setRequests((data.requests || []) as unknown as OrgRequest[]);
      }
    } catch (err) {
      console.error("Failed to load organization requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomCourseRequests = async () => {
    try {
      setLoadingCourseRequests(true);
      const { data, error } = await api.listAllCustomCourseRequestsApiV1AdminCustomCourseRequestsGet();
      if (!error && data) {
        setCustomCourseRequests((data.requests || []) as unknown as AdminCustomCourseRequest[]);
      }
    } catch (err) {
      console.error("Failed to load custom course requests:", err);
    } finally {
      setLoadingCourseRequests(false);
    }
  };

  useEffect(() => {
    void loadRequests();
    void loadCustomCourseRequests();
  }, []);

  const handleApprove = async (orgId: number) => {
    try {
      setActionLoadingId(orgId);
      const { error } = await api.approveOrganizationRequestApiV1AdminOrganizationRequestsOrgIdApprovePost({
        path: { org_id: orgId },
      });
      if (!error) {
        await loadRequests();
      }
    } catch (err) {
      console.error("Failed to approve:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectModalOrg) return;
    try {
      setActionLoadingId(rejectModalOrg.id);
      const { error } = await api.rejectOrganizationRequestApiV1AdminOrganizationRequestsOrgIdRejectPost({
        path: { org_id: rejectModalOrg.id },
        body: { rejection_reason: rejectionReason },
      });
      if (!error) {
        setRejectModalOrg(null);
        setRejectionReason("");
        await loadRequests();
      }
    } catch (err) {
      console.error("Failed to reject:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredRequests = requests.filter((r) => {
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    const matchesSearch =
      !searchQuery.trim() ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.applicant_email && r.applicant_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.industry && r.industry.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white px-4 py-3 shadow-xs sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                currentTab === "course_requests"
                  ? "bg-purple-50 text-purple-600"
                  : "bg-indigo-50 text-indigo-600"
              }`}
            >
              {currentTab === "course_requests" ? (
                <Sparkles className="h-4 w-4" />
              ) : (
                <Building2 className="h-4 w-4" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600">
                {currentTab === "course_requests"
                  ? "Custom Course Requests"
                  : "B2B Enterprise Admin"}
              </h1>
              {pendingCount > 0 && currentTab === "organizations" && (
                <span className="px-2.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold rounded-full animate-pulse">
                  {pendingCount} Pending Review
                </span>
              )}
            </div>
          </div>

          {/* Tab Switcher & Header Action */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center bg-slate-100 border border-slate-200 rounded-xl p-1 text-xs">
              <button
                type="button"
                onClick={() => handleTabChange("organizations")}
                className={`px-3.5 py-1.5 rounded-lg font-bold transition flex items-center gap-1.5 ${
                  currentTab === "organizations"
                    ? "bg-white text-indigo-600 shadow-xs border border-slate-200/60"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                Organizations ({requests.length})
              </button>
              <button
                type="button"
                onClick={() => handleTabChange("course_requests")}
                className={`px-3.5 py-1.5 rounded-lg font-bold transition flex items-center gap-1.5 ${
                  currentTab === "course_requests"
                    ? "bg-white text-purple-600 shadow-xs border border-slate-200/60"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                Custom Course Requests ({customCourseRequests.length})
              </button>
            </div>

            {currentTab === "course_requests" ? (
              <Link
                to="/admin/credits"
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition shadow-xs"
              >
                <Coins className="w-3.5 h-3.5 text-amber-500" />
                AI Credits &amp; Pricing
              </Link>
            ) : (
              <Link
                to="/b2b/register"
                target="_blank"
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition shadow-xs"
              >
                Open Registration Form
                <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              </Link>
            )}
          </div>
        </div>

        <p className="mt-1 text-sm text-slate-500 font-medium leading-tight">
          {currentTab === "course_requests"
            ? "Fulfill and oversee bespoke enterprise curricula, attached SOPs, and AI lesson generation."
            : "Review self-registered enterprise organizations and manage workspaces."}
        </p>
      </div>

      {/* Main Content View */}
      {currentTab === "course_requests" ? (
        <CustomCourseRequestsClient
          requests={customCourseRequests}
          loading={loadingCourseRequests}
          onRefresh={loadCustomCourseRequests}
        />
      ) : (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center bg-slate-100 border border-slate-200 rounded-xl p-1 text-xs">
              {["all", "pending", "approved", "rejected"].map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  className={`px-4 py-1.5 rounded-lg font-bold capitalize transition ${
                    statusFilter === st
                      ? "bg-white text-indigo-600 shadow-xs border border-slate-200/60"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {st} {st === "pending" && pendingCount > 0 ? `(${pendingCount})` : ""}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search companies, domains, emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-xs"
              />
            </div>
          </div>

          {/* Requests Table / Cards */}
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-slate-500 font-medium">Loading organization requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="py-16 text-center bg-white border border-slate-200 rounded-2xl shadow-xs">
              <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <h3 className="text-sm font-bold text-slate-800">No organizations found</h3>
              <p className="text-xs text-slate-500 mt-1">
                No registration requests match the current filters.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((req) => (
                <div
                  key={req.id}
                  className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs hover:border-slate-300 transition-colors flex flex-col lg:flex-row lg:items-center justify-between gap-6"
                >
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="text-base font-bold text-slate-900">{req.name}</h3>
                      <span className="text-xs font-mono text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-md border border-indigo-100 font-medium">
                        /org/{req.slug}
                      </span>
                      <span
                        className={`px-2.5 py-0.5 text-[10px] font-bold uppercase rounded-full border ${
                          req.status === "approved"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : req.status === "rejected"
                            ? "bg-rose-50 border-rose-200 text-rose-700"
                            : "bg-amber-50 border-amber-200 text-amber-700 animate-pulse"
                        }`}
                      >
                        {req.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">
                          Applicant
                        </span>
                        <span className="text-slate-800 font-medium">
                          {req.applicant_name || "Admin"}
                        </span>
                        <span className="text-[11px] block font-mono text-slate-500">
                          {req.applicant_email}
                        </span>
                      </div>

                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">
                          Industry &amp; Size
                        </span>
                        <span className="text-slate-700">{req.industry || "Not specified"}</span>
                        <span className="text-[11px] block text-slate-500">
                          {req.company_size || "—"}
                        </span>
                      </div>

                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">
                          Allowed Domains
                        </span>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {req.allowed_domains.map((d) => (
                            <span
                              key={d}
                              className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 rounded font-mono text-[10px]"
                            >
                              @{d}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">
                          Registration Date
                        </span>
                        <span className="text-slate-700">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {req.expected_skills.length > 0 && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-slate-500 font-semibold text-[11px]">Skill Focus:</span>
                        <div className="flex flex-wrap gap-1">
                          {req.expected_skills.map((s) => (
                            <span
                              key={s}
                              className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-medium rounded-md"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {req.rejection_reason && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-start gap-2">
                        <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <span>Rejection Reason: {req.rejection_reason}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2.5 shrink-0 border-t lg:border-t-0 pt-4 lg:pt-0 border-slate-100">
                    {req.status === "pending" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(req.id)}
                          disabled={actionLoadingId === req.id}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs flex items-center gap-1.5 transition"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Approve Workspace
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectModalOrg(req)}
                          disabled={actionLoadingId === req.id}
                          className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition"
                        >
                          <X className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </>
                    ) : req.status === "approved" ? (
                      <Link
                        to={`/org/${req.slug}/dashboard`}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs"
                      >
                        Open Dashboard
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleApprove(req.id)}
                        disabled={actionLoadingId === req.id}
                        className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold rounded-xl transition"
                      >
                        Re-Approve
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reject Organization Modal */}
      <AnimatePresence>
        {rejectModalOrg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <h3 className="text-base font-bold text-slate-900 mb-2">Reject Organization Request</h3>
              <p className="text-xs text-slate-600 mb-4">
                Are you sure you want to reject the registration for{" "}
                <strong className="text-slate-900">{rejectModalOrg.name}</strong>?
              </p>

              <form onSubmit={handleRejectSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Rejection Reason / Notes (Optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Unverified business domain, incomplete information..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none transition"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setRejectModalOrg(null)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition"
                  >
                    Confirm Rejection
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
